import { useCallback, useEffect, useRef, useState } from 'react';

export type TAutosaveState = 'saving' | 'saved';

interface IOptions<T extends object> {
  initial: T;
  send: (next: T, changed: Partial<T>) => Promise<unknown>;
  toConfirmed?: (next: T, response: unknown) => T;
  onError?: (error: unknown, rolledBack: (keyof T)[], confirmed: T) => void;
  onSaved?: (sent: Partial<T>, confirmed: T, pendingKeys: (keyof T)[]) => void;
  savedMs?: number;
}

const isSameValue = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b);

const DEFAULT_SAVED_MS = 2000;

export const useAutosaveQueue = <T extends object>({
  initial,
  send,
  toConfirmed,
  onError,
  onSaved,
  savedMs = DEFAULT_SAVED_MS,
}: IOptions<T>) => {
  const [confirmed, setConfirmed] = useState<T>(initial);
  const [saveStates, setSaveStates] = useState<Partial<Record<keyof T, TAutosaveState>>>({});
  const [isSaving, setIsSaving] = useState(false);

  const confirmedRef = useRef<T>(initial);
  const committedRef = useRef<Partial<T>>({});
  const inFlightRef = useRef(false);
  const sentRef = useRef<Partial<T>>({});
  const pendingRef = useRef(false);
  const lastRunOkRef = useRef(true);
  const waitersRef = useRef<((ok: boolean) => void)[]>([]);
  const timersRef = useRef<Partial<Record<keyof T, ReturnType<typeof setTimeout>>>>({});
  const optionsRef = useRef({ send, toConfirmed, onError, onSaved, savedMs });
  const mountedRef = useRef(true);

  optionsRef.current = { send, toConfirmed, onError, onSaved, savedMs };

  useEffect(() => {
    const timers = timersRef.current;

    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      Object.values(timers).forEach((timer) => clearTimeout(timer as ReturnType<typeof setTimeout>));
    };
  }, []);

  const setFieldStates = useCallback((keys: (keyof T)[], state?: TAutosaveState) => {
    if (!mountedRef.current) {
      return;
    }

    keys.forEach((key) => {
      const timer = timersRef.current[key];

      if (timer) {
        clearTimeout(timer);
        delete timersRef.current[key];
      }
    });

    setSaveStates((prev) => {
      const next = { ...prev };

      keys.forEach((key) => {
        if (state) {
          next[key] = state;
        } else {
          delete next[key];
        }
      });

      return next;
    });

    if (state === 'saved') {
      keys.forEach((key) => {
        timersRef.current[key] = setTimeout(() => {
          delete timersRef.current[key];
          setSaveStates((prev) => {
            if (prev[key] !== 'saved') {
              return prev;
            }

            const next = { ...prev };
            delete next[key];
            return next;
          });
        }, optionsRef.current.savedMs);
      });
    }
  }, []);

  const settleWaiters = useCallback(() => {
    const ok = lastRunOkRef.current;
    const waiters = waitersRef.current;

    waitersRef.current = [];
    lastRunOkRef.current = true;
    waiters.forEach((resolve) => resolve(ok));
  }, []);

  const clearSavingStates = useCallback((keys: (keyof T)[]) => {
    if (!keys.length || !mountedRef.current) {
      return;
    }

    setSaveStates((prev) => {
      const savingKeys = keys.filter((key) => prev[key] === 'saving');

      if (!savingKeys.length) {
        return prev;
      }

      const next = { ...prev };
      savingKeys.forEach((key) => delete next[key]);
      return next;
    });
  }, []);

  const updateIsSaving = useCallback((next: boolean) => {
    if (mountedRef.current) {
      setIsSaving(next);
    }
  }, []);

  const run = useCallback(async () => {
    const base = confirmedRef.current;
    const committed = committedRef.current;
    const committedKeys = Object.keys(committed) as (keyof T)[];
    const changedKeys = committedKeys.filter((key) => !isSameValue(committed[key], base[key]));
    const unchangedKeys = committedKeys.filter((key) => !changedKeys.includes(key));

    committedRef.current = changedKeys.reduce<Partial<T>>((acc, key) => {
      acc[key] = committed[key];
      return acc;
    }, {});

    clearSavingStates(unchangedKeys);

    if (!changedKeys.length) {
      pendingRef.current = false;
      updateIsSaving(false);
      settleWaiters();
      return;
    }

    const sent = { ...committedRef.current };
    const next = { ...base, ...sent } as T;

    inFlightRef.current = true;
    sentRef.current = sent;
    pendingRef.current = false;
    updateIsSaving(true);
    setFieldStates(changedKeys, 'saving');

    try {
      const response = await optionsRef.current.send(next, sent);

      const nextConfirmed = optionsRef.current.toConfirmed ? optionsRef.current.toConfirmed(next, response) : next;
      const stillCommitted = committedRef.current;
      const settledKeys = changedKeys.filter((key) => isSameValue(stillCommitted[key], sent[key]));

      settledKeys.forEach((key) => delete stillCommitted[key]);
      confirmedRef.current = nextConfirmed;

      if (mountedRef.current) {
        setConfirmed(nextConfirmed);
        setFieldStates(settledKeys, 'saved');
        optionsRef.current.onSaved?.(sent, nextConfirmed, Object.keys(stillCommitted) as (keyof T)[]);
      }
    } catch (error) {
      const stillCommitted = committedRef.current;
      const rolledBack = changedKeys.filter((key) => isSameValue(stillCommitted[key], sent[key]));

      rolledBack.forEach((key) => delete stillCommitted[key]);
      setFieldStates(rolledBack);
      lastRunOkRef.current = false;

      if (mountedRef.current) {
        optionsRef.current.onError?.(error, rolledBack, confirmedRef.current);
      }
    } finally {
      inFlightRef.current = false;
      sentRef.current = {};

      if (pendingRef.current) {
        run();
      } else {
        updateIsSaving(false);
        settleWaiters();
      }
    }
  }, [setFieldStates, clearSavingStates, settleWaiters, updateIsSaving]);

  const commit = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      const committed = committedRef.current;
      const effective = key in committed ? committed[key] : confirmedRef.current[key];

      if (isSameValue(effective, value)) {
        return;
      }

      committed[key] = value;

      if (inFlightRef.current) {
        pendingRef.current = true;
      } else {
        run();
      }
    },
    [run],
  );

  // Drops a value that is still queued; one already sent cannot be taken back.
  const discard = useCallback(<K extends keyof T>(key: K) => {
    const committed = committedRef.current;

    if (key in committed && !(key in sentRef.current)) {
      delete committed[key];
    }
  }, []);

  const flush = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        if (!inFlightRef.current && !pendingRef.current) {
          resolve(true);
          return;
        }

        waitersRef.current.push(resolve);
      }),
    [],
  );

  const getLatestValue = useCallback(<K extends keyof T>(key: K): T[K] => {
    const committed = committedRef.current;

    return (key in committed ? committed[key] : confirmedRef.current[key]) as T[K];
  }, []);

  return { confirmed, saveStates, isSaving, commit, discard, flush, getLatestValue };
};

export default useAutosaveQueue;
